import {
  CreditCardVendors,
  type CreditCardVendorEnum,
} from '@webapp/core/entities/external/CreditCardVendor'
import { type CurrencyName } from '@webapp/core/entities/external/Currency'
import {
  CardDisplayStatuses,
  type CardDisplayStatusEnum,
} from '@webapp/modules/spending-cards/entities/enums/CardDisplayStatusEnum'
import {
  CardStatuses,
  type CardStatusEnum,
} from '@webapp/modules/spending-cards/entities/enums/CardStatusEnum'
import {
  CardTypes,
  type CardTypeEnum,
} from '@webapp/modules/spending-cards/entities/enums/CardTypeEnum'
import { type IssuingProviderEnum } from '@webapp/modules/spending-cards/entities/enums/IssuingProvider'

export interface AdminCardConstructor {
  id: number
  externalId: string
  last4Digits: string
  type: CardTypeEnum
  provider: IssuingProviderEnum
  status: CardStatusEnum
  cardholder: {
    id: number
    userId: number
    email: string
    firstName: string
    lastName: string
    profileImage: string
    secondaryCategoryId?: number
  }
  createdAt: ISODateString
  updatedAt: ISODateString
  requireActivation: boolean
  balance: number
  currencyName: CurrencyName
  shippingAddress: Nullable<{
    addressLine1: string
    addressLine2: Nullable<string>
    postalCode: string
    city: string
    stateProvince: string
    country: string
  }>
}

/**
 * `AdminCard` entity represents the shape of a spending card as seen in admin views (Manage Cards, Reconciliation)
 */
export class AdminCard {
  id: number
  externalId: string
  last4Digits: string
  type: CardTypeEnum
  provider: IssuingProviderEnum
  status: CardStatusEnum
  cardholder: {
    id: number
    userId: number
    email: string
    firstName: string
    lastName: string
    profileImage: string
    secondaryCategoryId?: number
  }
  createdAt: ISODateString
  updatedAt: ISODateString
  requireActivation: boolean
  balance: number
  currencyName: CurrencyName
  vendor: CreditCardVendorEnum
  shippingAddress: Nullable<{
    addressLine1: string
    addressLine2: Nullable<string>
    postalCode: string
    city: string
    stateProvince: string
    country: string
  }>

  constructor(data: AdminCardConstructor) {
    this.id = data.id
    this.externalId = data.externalId
    this.last4Digits = data.last4Digits
    this.type = data.type
    this.provider = data.provider
    this.status = data.status
    this.cardholder = {
      id: data.cardholder.id,
      userId: data.cardholder.userId,
      email: data.cardholder.email,
      firstName: data.cardholder.firstName,
      lastName: data.cardholder.lastName,
      profileImage: data.cardholder.profileImage || '',
      secondaryCategoryId: data.cardholder.secondaryCategoryId || undefined,
    }
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.requireActivation = data.requireActivation
    this.balance = data.balance
    this.currencyName = data.currencyName
    this.vendor = CreditCardVendors.VISA
    this.shippingAddress = data.shippingAddress || null
  }

  get isPhysicalCard(): boolean {
    return this.type === CardTypes.PHYSICAL
  }

  get isVirtualCard(): boolean {
    return this.type === CardTypes.VIRTUAL
  }

  get cardDisplayStatus(): CardDisplayStatusEnum | undefined {
    switch (this.status) {
      case CardStatuses.ACTIVE:
        return CardDisplayStatuses.ACTIVE
      case CardStatuses.INACTIVE:
        if (this.requireActivation) {
          return CardDisplayStatuses.PENDING
        }
        return CardDisplayStatuses.LOCKED
      case CardStatuses.CANCELED:
        return CardDisplayStatuses.CANCELED
      default:
        return CardDisplayStatuses.UNKNOWN
    }
  }

  get isActive(): boolean {
    return this.status === CardStatuses.ACTIVE
  }

  get isPendingActivation(): boolean {
    return this.requireActivation && this.status === CardStatuses.INACTIVE
  }

  get isLocked(): boolean {
    return !this.requireActivation && this.status === CardStatuses.INACTIVE
  }

  get isCanceled(): boolean {
    return this.status === CardStatuses.CANCELED
  }

  get cardholderFullName(): string {
    return `${this.cardholder.firstName} ${this.cardholder.lastName}`
  }

  get formattedShippingAddress(): string | null {
    if (!this.shippingAddress) return null

    return [
      this.shippingAddress.addressLine1,
      this.shippingAddress.addressLine2,
      this.shippingAddress.city,
      this.shippingAddress.stateProvince,
      this.shippingAddress.postalCode,
      this.shippingAddress.country,
    ]
      .filter(Boolean)
      .join(', ')
  }
}
